import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Switch,
  FormControlLabel,
  Snackbar,
  Tab,
  Tabs
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  DateRange as DateIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import api from '../../services/auth';
import { getCurrentUser } from '../../services/auth';
import { format, parseISO, addMonths, addYears, addDays, isAfter } from 'date-fns';
import { fr } from 'date-fns/locale';

const AbonnementPage = () => {
  const [abonnements, setAbonnements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAbonnement, setSelectedAbonnement] = useState(null);
  const [dialogMode, setDialogMode] = useState('add');
  const [entreprises, setEntreprises] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [tabValue, setTabValue] = useState(0); // 0: Tous, 1: Actifs, 2: Expirés

  // Plan options
  const planOptions = [
    { value: 'mensuel', label: 'Mensuel' },
    { value: 'trimestriel', label: 'Trimestriel' },
    { value: 'annuel', label: 'Annuel' }
  ];

  // Form state
  const [formData, setFormData] = useState({
    entreprise: '',
    plan: 'mensuel',
    montant: '',
    date_debut: format(new Date(), 'yyyy-MM-dd'),
    date_fin: '',
    est_paye: false,
    date_paiement: '',
    notes: ''
  });

  const [formErrors, setFormErrors] = useState({});

  // Récupérer l'utilisateur connecté au montage
  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
    
    if (!user) {
      window.location.href = '/login';
      return;
    }
    
    fetchAbonnements();
    fetchEntreprises();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const fetchAbonnements = async () => {
    try {
      setLoading(true);
      const response = await api.get('/abonnements/');
      
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      } else if (response.data?.data) {
        data = response.data.data;
      }
      
      // Trier par date de création décroissante
      data.sort((a, b) => new Date(b.date_creation || b.created_at) - new Date(a.date_creation || a.created_at));
      
      setAbonnements(data);
      setError('');
    } catch (err) {
      console.error('Erreur abonnements:', err);
      let errorMsg = 'Erreur lors du chargement des abonnements';
      
      if (err.response?.status === 401) {
        errorMsg = 'Session expirée, veuillez vous reconnecter';
      } else if (err.response?.status === 403) {
        errorMsg = 'Permission refusée';
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      
      setError(errorMsg);
      setAbonnements([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntreprises = async () => {
    try {
      const response = await api.get('/entreprises/');
      
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      } else if (response.data?.data) {
        data = response.data.data;
      }
      
      // Filtrer selon les permissions
      let filteredEntreprises = data;
      if (currentUser?.role === 'proprietaire') {
        filteredEntreprises = data.filter(e => e.id === currentUser.entreprise);
      }
      
      setEntreprises(filteredEntreprises);
      
      // Pré-sélectionner l'entreprise pour les non-super_admins
      if (currentUser && currentUser.role !== 'super_admin' && currentUser.entreprise) {
        setFormData(prev => ({
          ...prev,
          entreprise: currentUser.entreprise
        }));
      }
    } catch (err) {
      console.error('Erreur entreprises:', err);
      setEntreprises([]);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const calculateDateFin = (dateDebut, plan) => {
    const date = new Date(dateDebut);
    
    switch(plan) {
      case 'mensuel':
        return addMonths(date, 1);
      case 'trimestriel':
        return addMonths(date, 3);
      case 'annuel':
        return addYears(date, 1);
      default:
        return addMonths(date, 1);
    }
  };

  const calculateMontant = (plan) => {
    switch(plan) {
      case 'mensuel':
        return 50.00;
      case 'trimestriel':
        return 135.00; // 45 par mois
      case 'annuel':
        return 480.00; // 40 par mois
      default:
        return 50.00;
    }
  };

  const handleOpenDialog = (mode, abonnement = null) => {
    setDialogMode(mode);
    setSelectedAbonnement(abonnement);
    setFormErrors({});
    
    if (mode === 'edit' || mode === 'view') {
      setFormData({
        entreprise: abonnement.entreprise?.id || abonnement.entreprise || '',
        plan: abonnement.plan || 'mensuel',
        montant: abonnement.montant || '',
        date_debut: abonnement.date_debut
          ? format(parseISO(abonnement.date_debut), 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd'),
        date_fin: abonnement.date_fin
          ? format(parseISO(abonnement.date_fin), 'yyyy-MM-dd')
          : format(calculateDateFin(abonnement.date_debut || new Date(), abonnement.plan || 'mensuel'), 'yyyy-MM-dd'),
        est_paye: abonnement.est_paye || false,
        date_paiement: abonnement.date_paiement
          ? format(parseISO(abonnement.date_paiement), 'yyyy-MM-dd')
          : '',
        notes: abonnement.notes || ''
      });
    } else {
      // Reset form for add mode
      const dateDebut = new Date();
      const plan = 'mensuel';
      const dateFin = calculateDateFin(dateDebut, plan);
      const montant = calculateMontant(plan);
      
      const newFormData = {
        entreprise: '',
        plan: plan,
        montant: montant.toString(),
        date_debut: format(dateDebut, 'yyyy-MM-dd'),
        date_fin: format(dateFin, 'yyyy-MM-dd'),
        est_paye: false,
        date_paiement: '',
        notes: ''
      };
      
      // Pré-sélectionner l'entreprise pour les non-super_admins
      if (currentUser && currentUser.role !== 'super_admin' && currentUser.entreprise) {
        newFormData.entreprise = currentUser.entreprise;
      }
      
      setFormData(newFormData);
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAbonnement(null);
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };
    
    // Si le plan ou la date de début change, recalculer la date de fin et le montant
    if (name === 'plan' || name === 'date_debut') {
      if (name === 'plan') {
        newFormData.montant = calculateMontant(value).toString();
      }
      
      if (newFormData.date_debut) {
        newFormData.date_fin = format(
          calculateDateFin(newFormData.date_debut, newFormData.plan),
          'yyyy-MM-dd'
        );
      }
    }
    
    setFormData(newFormData);
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    const newFormData = {
      ...formData,
      [name]: checked
    };
    
    // Si on marque comme payé et qu'il n'y a pas de date de paiement, mettre la date actuelle
    if (name === 'est_paye' && checked && !formData.date_paiement) {
      newFormData.date_paiement = format(new Date(), 'yyyy-MM-dd');
    }
    
    // Si on marque comme non payé, effacer la date de paiement
    if (name === 'est_paye' && !checked) {
      newFormData.date_paiement = '';
    }
    
    setFormData(newFormData);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.entreprise) errors.entreprise = 'Entreprise requise';
    if (!formData.plan) errors.plan = 'Plan requis';
    if (!formData.montant || parseFloat(formData.montant) <= 0) errors.montant = 'Montant invalide';
    if (!formData.date_debut) errors.date_debut = 'Date de début requise';
    if (!formData.date_fin) errors.date_fin = 'Date de fin requise';
    
    if (formData.date_debut && formData.date_fin) {
      const dateDebut = new Date(formData.date_debut);
      const dateFin = new Date(formData.date_fin);
      
      if (dateFin <= dateDebut) {
        errors.date_fin = 'La date de fin doit être après la date de début';
      }
    }
    
    if (formData.est_paye && !formData.date_paiement) {
      errors.date_paiement = 'Date de paiement requise pour un abonnement payé';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showSnackbar('Veuillez corriger les erreurs dans le formulaire', 'error');
      return;
    }
    
    setActionLoading(true);
    
    try {
      const dataToSend = {
        ...formData,
        montant: parseFloat(formData.montant),
        est_paye: formData.est_paye,
        date_paiement: formData.est_paye && formData.date_paiement ? formData.date_paiement : null
      };
      
      console.log('Données envoyées:', dataToSend);
      
      if (dialogMode === 'add') {
        const response = await api.post('/abonnements/', dataToSend);
        console.log('Abonnement créé:', response.data);
        showSnackbar('Abonnement créé avec succès', 'success');
      } else if (dialogMode === 'edit') {
        const response = await api.put(`/abonnements/${selectedAbonnement.id}/`, dataToSend);
        console.log('Abonnement modifié:', response.data);
        showSnackbar('Abonnement modifié avec succès', 'success');
      }
      
      await fetchAbonnements();
      handleCloseDialog();
      
    } catch (err) {
      console.error('Erreur enregistrement:', err);
      
      if (err.response?.status === 400) {
        const apiErrors = err.response.data;
        console.error('Erreurs de validation:', apiErrors);
        
        const formattedErrors = {};
        Object.keys(apiErrors).forEach(key => {
          if (Array.isArray(apiErrors[key])) {
            formattedErrors[key] = apiErrors[key].join(', ');
          } else {
            formattedErrors[key] = apiErrors[key];
          }
        });
        
        setFormErrors(formattedErrors);
        showSnackbar('Erreurs de validation, vérifiez le formulaire', 'error');
      } else if (err.response?.status === 403) {
        showSnackbar('Permission refusée', 'error');
      } else if (err.response?.status === 401) {
        showSnackbar('Session expirée', 'error');
      } else if (err.response?.status === 500) {
        showSnackbar('Erreur serveur, veuillez réessayer', 'error');
      } else {
        showSnackbar('Erreur lors de l\'enregistrement', 'error');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet abonnement ?')) return;
    
    setActionLoading(true);
    
    try {
      await api.delete(`/abonnements/${id}/`);
      showSnackbar('Abonnement supprimé avec succès', 'success');
      await fetchAbonnements();
      
    } catch (err) {
      console.error('Erreur suppression:', err);
      
      if (err.response?.status === 403) {
        showSnackbar('Permission refusée pour la suppression', 'error');
      } else if (err.response?.status === 400) {
        showSnackbar('Impossible de supprimer cet abonnement', 'error');
      } else if (err.response?.status === 401) {
        showSnackbar('Session expirée', 'error');
      } else if (err.response?.status === 500) {
        showSnackbar('Erreur serveur lors de la suppression', 'error');
      } else {
        showSnackbar('Erreur lors de la suppression', 'error');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleTogglePaye = async (id, currentStatus) => {
    setActionLoading(true);
    
    try {
      // Récupérer l'abonnement actuel
      const response = await api.get(`/abonnements/${id}/`);
      const abonnement = response.data;
      
      const newData = {
        est_paye: !currentStatus,
        date_paiement: !currentStatus ? format(new Date(), 'yyyy-MM-dd') : null
      };
      
      await api.patch(`/abonnements/${id}/`, newData);
      
      await fetchAbonnements();
      showSnackbar(`Abonnement ${!currentStatus ? 'marqué comme payé' : 'marqué comme non payé'}`, 'success');
      
    } catch (err) {
      console.error('Erreur changement statut paiement:', err);
      
      if (err.response?.status === 401) {
        showSnackbar('Session expirée', 'error');
      } else {
        showSnackbar('Erreur lors du changement de statut', 'error');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleRenouveler = async (abonnement) => {
    if (!window.confirm(`Renouveler l'abonnement pour ${abonnement.entreprise_detail?.nom || 'cette entreprise'} ?`)) return;
    
    setActionLoading(true);
    
    try {
      const dateDebut = new Date(abonnement.date_fin);
      const dateFin = calculateDateFin(dateDebut, abonnement.plan);
      const montant = calculateMontant(abonnement.plan);
      
      const newAbonnement = {
        entreprise: abonnement.entreprise?.id || abonnement.entreprise,
        plan: abonnement.plan,
        montant: montant,
        date_debut: format(dateDebut, 'yyyy-MM-dd'),
        date_fin: format(dateFin, 'yyyy-MM-dd'),
        est_paye: false,
        notes: `Renouvellement de l'abonnement #${abonnement.id}`
      };
      
      const response = await api.post('/abonnements/', newAbonnement);
      console.log('Abonnement renouvelé:', response.data);
      
      await fetchAbonnements();
      showSnackbar('Abonnement renouvelé avec succès', 'success');
      
    } catch (err) {
      console.error('Erreur renouvellement:', err);
      
      if (err.response?.status === 401) {
        showSnackbar('Session expirée', 'error');
      } else {
        showSnackbar('Erreur lors du renouvellement', 'error');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAbonnements();
    showSnackbar('Données actualisées', 'info');
  };

  const canCreateAbonnement = () => {
    if (!currentUser) return false;
    return currentUser.role === 'super_admin';
  };

  const canEditAbonnement = (abonnement) => {
    if (!currentUser) return false;
    if (currentUser.role === 'super_admin') return true;
    if (currentUser.role === 'proprietaire' && currentUser.entreprise === abonnement.entreprise) return true;
    return false;
  };

  const canDeleteAbonnement = (abonnement) => {
    if (!currentUser) return false;
    if (currentUser.role === 'super_admin') return true;
    return false;
  };

  const safeAbonnements = Array.isArray(abonnements) ? abonnements : [];

  const filteredAbonnements = safeAbonnements.filter(abonnement => {
    if (!abonnement) return false;
    
    const searchLower = search.toLowerCase();
    const entrepriseNom = abonnement.entreprise_detail?.nom || '';
    const planLabel = planOptions.find(p => p.value === abonnement.plan)?.label || '';
    
    return (
      entrepriseNom.toLowerCase().includes(searchLower) ||
      planLabel.toLowerCase().includes(searchLower) ||
      (abonnement.notes?.toLowerCase() || '').includes(searchLower)
    );
  });

  // Filtrer selon l'onglet sélectionné
  const getFilteredByTab = () => {
    const now = new Date();
    
    switch(tabValue) {
      case 0: // Tous
        return filteredAbonnements;
      case 1: // Actifs
        return filteredAbonnements.filter(a => {
          const dateFin = new Date(a.date_fin);
          return isAfter(dateFin, now) || format(dateFin, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
        });
      case 2: // Expirés
        return filteredAbonnements.filter(a => {
          const dateFin = new Date(a.date_fin);
          return isAfter(now, dateFin) && format(dateFin, 'yyyy-MM-dd') !== format(now, 'yyyy-MM-dd');
        });
      default:
        return filteredAbonnements;
    }
  };

  const tabFilteredAbonnements = getFilteredByTab();
  const paginatedAbonnements = tabFilteredAbonnements.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy', { locale: fr });
    } catch (e) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch (e) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const getPlanLabel = (plan) => {
    const planOption = planOptions.find(p => p.value === plan);
    return planOption ? planOption.label : plan;
  };

  const getPlanColor = (plan) => {
    const colors = {
      'mensuel': 'primary',
      'trimestriel': 'info',
      'annuel': 'success'
    };
    return colors[plan] || 'default';
  };

  const getStatusInfo = (abonnement) => {
    const now = new Date();
    const dateFin = new Date(abonnement.date_fin);
    const isActive = isAfter(dateFin, now) || format(dateFin, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
    
    if (!abonnement.est_paye) {
      return { label: 'Non payé', color: 'error' };
    } else if (isActive) {
      return { label: 'Actif', color: 'success' };
    } else {
      return { label: 'Expiré', color: 'warning' };
    }
  };

  const getDaysRemaining = (dateFin) => {
    const now = new Date();
    const fin = new Date(dateFin);
    const diffTime = fin - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading && !currentUser) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ p: 3, mb: 3, boxShadow: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Gestion des Abonnements
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tabFilteredAbonnements.length} abonnement(s) trouvé(s)
            </Typography>
          </Box>
          
          <Box display="flex" gap={2}>
            <Tooltip title="Actualiser les données">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={actionLoading || loading}
                sx={{ borderRadius: '8px' }}
              >
                Actualiser
              </Button>
            </Tooltip>
            
            {canCreateAbonnement() && (
              <Tooltip title="Ajouter un nouvel abonnement">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog('add')}
                  disabled={actionLoading}
                  sx={{ borderRadius: '8px', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                  Nouvel Abonnement
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Tabs pour filtrer */}
        <Box mb={3}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`Tous (${filteredAbonnements.length})`} />
            <Tab label={
              <Box display="flex" alignItems="center">
                Actifs
                <Chip 
                  label={filteredAbonnements.filter(a => {
                    const now = new Date();
                    const dateFin = new Date(a.date_fin);
                    return (isAfter(dateFin, now) || format(dateFin, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd')) && a.est_paye;
                  }).length}
                  size="small"
                  color="success"
                  sx={{ ml: 1, height: 20 }}
                />
              </Box>
            } />
            <Tab label={
              <Box display="flex" alignItems="center">
                Expirés
                <Chip 
                  label={filteredAbonnements.filter(a => {
                    const now = new Date();
                    const dateFin = new Date(a.date_fin);
                    return isAfter(now, dateFin) && format(dateFin, 'yyyy-MM-dd') !== format(now, 'yyyy-MM-dd');
                  }).length}
                  size="small"
                  color="warning"
                  sx={{ ml: 1, height: 20 }}
                />
              </Box>
            } />
          </Tabs>
        </Box>

        <Box mb={3}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Rechercher par entreprise, plan, notes..."
            value={search}
            onChange={handleSearch}
            disabled={actionLoading}
            sx={{ 
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton 
                    size="small" 
                    onClick={() => setSearch('')}
                    disabled={actionLoading}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: '8px' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Entreprise</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Plan</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Montant</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Période</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Paiement</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box display="flex" justifyContent="center" alignItems="center">
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography color="text.secondary">
                        Chargement des abonnements...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : paginatedAbonnements.length > 0 ? (
                paginatedAbonnements.map((abonnement) => {
                  const statusInfo = getStatusInfo(abonnement);
                  const daysRemaining = getDaysRemaining(abonnement.date_fin);
                  
                  return (
                    <TableRow 
                      key={abonnement.id}
                      hover
                      sx={{ 
                        '&:hover': { bgcolor: 'action.hover' },
                        opacity: abonnement.est_paye ? 1 : 0.8
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" fontWeight="medium">
                            {abonnement.entreprise_detail?.nom || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getPlanLabel(abonnement.plan)}
                          color={getPlanColor(abonnement.plan)}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <MoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(abonnement.montant)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column">
                          <Typography variant="body2">
                            <CalendarIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                            {formatDate(abonnement.date_debut)} → {formatDate(abonnement.date_fin)}
                          </Typography>
                          {abonnement.est_paye && daysRemaining > 0 && (
                            <Typography variant="caption" color={daysRemaining <= 7 ? 'error' : 'warning'}>
                              {daysRemaining} jour(s) restant(s)
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusInfo.color === 'success' ? <CheckIcon /> : statusInfo.color === 'error' ? <CancelIcon /> : null}
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>
                        {abonnement.est_paye ? (
                          <Box display="flex" alignItems="center">
                            <CheckIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
                            <Box>
                              <Typography variant="body2">
                                {formatDate(abonnement.date_paiement)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Payé
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center">
                            <CancelIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
                            <Typography variant="body2" color="text.secondary">
                              En attente
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Voir détails">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog('view', abonnement)}
                              color="info"
                              disabled={actionLoading}
                              sx={{ 
                                bgcolor: 'info.light', 
                                '&:hover': { bgcolor: 'info.main', color: 'white' }
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {canEditAbonnement(abonnement) && (
                            <Tooltip title="Modifier">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog('edit', abonnement)}
                                color="primary"
                                disabled={actionLoading}
                                sx={{ 
                                  bgcolor: 'primary.light', 
                                  '&:hover': { bgcolor: 'primary.main', color: 'white' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {canEditAbonnement(abonnement) && (
                            <Tooltip title={abonnement.est_paye ? "Marquer comme non payé" : "Marquer comme payé"}>
                              <IconButton
                                size="small"
                                onClick={() => handleTogglePaye(abonnement.id, abonnement.est_paye)}
                                color={abonnement.est_paye ? "warning" : "success"}
                                disabled={actionLoading}
                                sx={{ 
                                  bgcolor: abonnement.est_paye ? 'warning.light' : 'success.light', 
                                  '&:hover': { 
                                    bgcolor: abonnement.est_paye ? 'warning.main' : 'success.main', 
                                    color: 'white' 
                                  }
                                }}
                              >
                                <PaymentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {canEditAbonnement(abonnement) && abonnement.est_paye && (
                            <Tooltip title="Renouveler">
                              <IconButton
                                size="small"
                                onClick={() => handleRenouveler(abonnement)}
                                color="secondary"
                                disabled={actionLoading}
                                sx={{ 
                                  bgcolor: 'secondary.light', 
                                  '&:hover': { bgcolor: 'secondary.main', color: 'white' }
                                }}
                              >
                                <DateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {canDeleteAbonnement(abonnement) && (
                            <Tooltip title="Supprimer">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(abonnement.id)}
                                color="error"
                                disabled={actionLoading}
                                sx={{ 
                                  bgcolor: 'error.light', 
                                  '&:hover': { bgcolor: 'error.main', color: 'white' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {search ? 'Aucun abonnement ne correspond à votre recherche' : 'Aucun abonnement trouvé'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          
          {tabFilteredAbonnements.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={tabFilteredAbonnements.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} sur ${count}`
              }
              sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}
            />
          )}
        </Paper>
      </Card>

      {/* Dialog pour ajouter/modifier/voir */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px' }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px'
        }}>
          <Box display="flex" alignItems="center">
            {dialogMode === 'add' ? <AddIcon sx={{ mr: 1 }} /> : 
             dialogMode === 'edit' ? <EditIcon sx={{ mr: 1 }} /> : 
             <ViewIcon sx={{ mr: 1 }} />}
            {dialogMode === 'add' ? 'Nouvel Abonnement' : 
             dialogMode === 'edit' ? 'Modifier l\'Abonnement' : 
             'Détails de l\'Abonnement'}
          </Box>
          
          {selectedAbonnement && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>
              {selectedAbonnement.entreprise_detail?.nom && `Entreprise: ${selectedAbonnement.entreprise_detail.nom}`}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                error={!!formErrors.entreprise}
                disabled={dialogMode === 'view' || actionLoading || (currentUser && currentUser.role !== 'super_admin')}
                sx={{ mt: 1 }}
              >
                <InputLabel>Entreprise *</InputLabel>
                <Select
                  name="entreprise"
                  value={formData.entreprise}
                  onChange={handleInputChange}
                  label="Entreprise *"
                >
                  {entreprises.map((entreprise) => (
                    <MenuItem key={entreprise.id} value={entreprise.id}>
                      {entreprise.nom}
                      {entreprise.module_actif && ` (${entreprise.module_actif})`}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.entreprise && (
                  <Typography variant="caption" color="error">
                    {formErrors.entreprise}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                error={!!formErrors.plan}
                disabled={dialogMode === 'view' || actionLoading}
              >
                <InputLabel>Plan *</InputLabel>
                <Select
                  name="plan"
                  value={formData.plan}
                  onChange={handleInputChange}
                  label="Plan *"
                >
                  {planOptions.map((plan) => (
                    <MenuItem key={plan.value} value={plan.value}>
                      {plan.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.plan && (
                  <Typography variant="caption" color="error">
                    {formErrors.plan}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Montant"
                name="montant"
                type="number"
                value={formData.montant}
                onChange={handleInputChange}
                error={!!formErrors.montant}
                helperText={formErrors.montant}
                disabled={dialogMode === 'view' || actionLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  inputProps: { 
                    min: 0,
                    step: 0.01
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.est_paye}
                    onChange={handleSwitchChange}
                    name="est_paye"
                    color="primary"
                    disabled={dialogMode === 'view' || actionLoading}
                  />
                }
                label="Abonnement payé"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date de début *"
                name="date_debut"
                type="date"
                value={formData.date_debut}
                onChange={handleInputChange}
                error={!!formErrors.date_debut}
                helperText={formErrors.date_debut}
                disabled={dialogMode === 'view' || actionLoading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date de fin *"
                name="date_fin"
                type="date"
                value={formData.date_fin}
                onChange={handleInputChange}
                error={!!formErrors.date_fin}
                helperText={formErrors.date_fin || 'Calculée automatiquement selon le plan'}
                disabled={dialogMode === 'view' || actionLoading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date de paiement"
                name="date_paiement"
                type="date"
                value={formData.date_paiement}
                onChange={handleInputChange}
                error={!!formErrors.date_paiement}
                helperText={formErrors.date_paiement}
                disabled={dialogMode === 'view' || actionLoading || !formData.est_paye}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                multiline
                rows={3}
                disabled={dialogMode === 'view' || actionLoading}
                placeholder="Informations supplémentaires sur l'abonnement..."
              />
            </Grid>
            
            {/* Display API errors */}
            {Object.keys(formErrors).filter(key => key !== 'non_field_errors').length > 0 && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Erreurs de validation :</Typography>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {Object.entries(formErrors)
                      .filter(([key]) => key !== 'non_field_errors')
                      .map(([key, value]) => (
                        <li key={key}>
                          <strong>{key}</strong>: {value}
                        </li>
                      ))
                    }
                  </ul>
                </Alert>
              </Grid>
            )}
            
            {formErrors.non_field_errors && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 2 }}>
                  {Array.isArray(formErrors.non_field_errors) 
                    ? formErrors.non_field_errors.join(', ')
                    : formErrors.non_field_errors}
                </Alert>
              </Grid>
            )}
            
            {dialogMode === 'view' && selectedAbonnement && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Informations supplémentaires:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Créé le:</strong> {formatDateTime(selectedAbonnement.date_creation || selectedAbonnement.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Jours restants:</strong> {getDaysRemaining(selectedAbonnement.date_fin)} jour(s)
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Statut complet:</strong> {selectedAbonnement.est_paye ? 'Payé' : 'Non payé'} • 
                        {getStatusInfo(selectedAbonnement).label}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button 
            onClick={handleCloseDialog} 
            disabled={actionLoading}
            variant="outlined"
            sx={{ borderRadius: '8px' }}
          >
            {dialogMode === 'view' ? 'Fermer' : 'Annuler'}
          </Button>
          
          {dialogMode !== 'view' && (
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : null}
              sx={{ borderRadius: '8px' }}
            >
              {dialogMode === 'add' ? 'Créer' : 'Enregistrer'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AbonnementPage;